import React from 'react';
import { Link } from 'react-router-dom';
import Seo from '../components/Seo';
import storyImage from '../assets/images/testimonial_1.png';

const story = {
  title: 'Mark and Lisa',
  description:
    'Our connection started with a simple conversation and grew into something deeper. We shared real values, everyday chemistry, and a future we both wanted. SLAMMMM helped us find that spark in a way that felt natural, honest, and easy.',
  fullStory:
    'Mark was looking for something more than a swipe. Lisa wanted a connection grounded in trust and shared values. On SLAMMMM, they found more than attraction—they found a rhythm that felt right. Their story is proof that the best relationships often begin with a real conversation, not a perfect first impression.',
  image: storyImage,
};

const highlights = [
  {
    title: 'Real compatibility',
    text: 'People connect beyond the surface with shared values, lifestyle fit, and genuine chemistry.',
  },
  {
    title: 'Honest conversations',
    text: 'SLAMMMM encourages real dialogue that helps people understand each other before emotional investment grows.',
  },
  {
    title: 'A better path to love',
    text: 'Instead of endless swiping, members can focus on what actually matters—mutual attraction, trust, and relationship goals.',
  },
];

const stories = [
  {
    title: 'Ava and Daniel',
    text: 'We were both tired of empty chats and surface-level matches. SLAMMMM gave us a clearer way to meet people with real intentions.',
  },
  {
    title: 'Sofia and James',
    text: 'The connection felt natural from the start. We talked, laughed, and discovered how much we had in common without the usual dating stress.',
  },
  {
    title: 'Priya and Noah',
    text: 'We were looking for something serious, and the platform helped us find the kind of connection built on trust, emotional alignment, and shared goals.',
  },
];

const LoveStories: React.FC = () => {
  return (
    <>
      <Seo
        title="SLAMMMM Love Stories | Real Stories of Love and Connection"
        description="Read real SLAMMMM love stories and discover how meaningful relationships begin with honesty, compatibility, and genuine connection."
      />

      <section style={{ background: '#f5f5f5', padding: '38px 0 60px' }}>
        <div className="container" style={{ maxWidth: 1180 }}>
          <div className="row align-items-center g-4 mb-4">
            <div className="col-lg-6">
              <div style={{ maxWidth: 620 }}>
                <span
                  style={{
                    display: 'inline-block',
                    background: '#fff',
                    border: '1px solid #e8e8e8',
                    borderRadius: 999,
                    padding: '8px 16px',
                    fontSize: '0.8rem',
                    fontWeight: 700,
                    letterSpacing: '0.08em',
                    textTransform: 'uppercase',
                    color: '#222',
                    marginBottom: 18,
                  }}
                >
                  Real love stories
                </span>
                <h1 style={{ fontSize: 'clamp(2.5rem, 4vw, 4.5rem)', fontWeight: 800, lineHeight: 1.06, margin: 0 }}>
                  They Found Love, So Can You
                </h1>
              </div>
            </div>

            <div className="col-lg-6">
              <p style={{ fontSize: '1.08rem', lineHeight: 1.8, color: '#4b5563', margin: 0 }}>
                The best love stories don’t start with pressure or perfect timing. They start with honesty, interest, and the courage to be seen. At SLAMMMM, people come together for real conversations, meaningful compatibility, and the kind of connection that turns into something lasting.
              </p>
            </div>
          </div>

          <div
            className="overflow-hidden"
            style={{
              background: '#1d2a4c',
              borderRadius: 18,
              boxShadow: '0 12px 30px rgba(0,0,0,0.08)',
            }}
          >
            <div className="row g-0 align-items-center">
              <div className="col-lg-5 p-3 p-lg-4">
                <div className="text-white" style={{ maxWidth: 430, color: '#ffffff' }}>
                  <h3 style={{ fontSize: '2.3rem', fontWeight: 700, lineHeight: 1.2, marginBottom: '0.8rem', color: '#ffffff' }}>
                    {story.title}
                  </h3>
                  <p style={{ fontSize: '1.05rem', lineHeight: 1.8, color: '#edf3ff', margin: 0 }}>
                    {story.description}
                  </p>
                </div>
              </div>

              <div className="col-lg-7 position-relative p-2 p-lg-3">
                <div className="position-relative overflow-hidden" style={{ borderRadius: 18, minHeight: 320 }}>
                  <img
                    src={story.image}
                    alt={story.title}
                    style={{ display: 'block', width: '100%', height: '100%', objectFit: 'cover' }}
                  />
                </div>

                <button
                  type="button"
                  aria-label="Previous story"
                  className="position-absolute top-50 start-0 translate-middle-y d-flex align-items-center justify-content-center rounded-circle border border-2 border-warning bg-white text-warning"
                  style={{ width: 42, height: 42, left: '1rem', fontSize: '1.5rem', fontWeight: 700, boxShadow: '0 8px 18px rgba(0,0,0,0.12)' }}
                >
                  ←
                </button>

                <button
                  type="button"
                  aria-label="Next story"
                  className="position-absolute top-50 end-0 translate-middle-y d-flex align-items-center justify-content-center rounded-circle border border-2 border-warning bg-white text-warning"
                  style={{ width: 42, height: 42, right: '1rem', fontSize: '1.5rem', fontWeight: 700, boxShadow: '0 8px 18px rgba(0,0,0,0.12)' }}
                >
                  →
                </button>
              </div>
            </div>
          </div>

          <div className="mt-4">
            <div className="row g-4">
              <div className="col-lg-8">
                <div style={{ background: '#fff', borderRadius: 18, padding: '2rem', boxShadow: '0 10px 28px rgba(0,0,0,0.04)' }}>
                  <h3 style={{ fontSize: '1.7rem', fontWeight: 700, marginBottom: '1rem' }}>What made the connection work?</h3>
                  <p style={{ fontSize: '1.02rem', lineHeight: 1.8, color: '#4b5563', margin: 0 }}>
                    {story.fullStory}
                  </p>
                </div>
              </div>

              <div className="col-lg-4">
                <div style={{ background: '#fff', borderRadius: 18, padding: '2rem', boxShadow: '0 10px 28px rgba(0,0,0,0.04)' }}>
                  <h3 style={{ fontSize: '1.5rem', fontWeight: 700, marginBottom: '1.2rem' }}>Why SLAMMMM works</h3>
                  <div className="d-flex flex-column gap-3">
                    {highlights.map((item) => (
                      <div key={item.title}>
                        <h4 style={{ fontSize: '1.05rem', fontWeight: 700, marginBottom: '0.35rem' }}>{item.title}</h4>
                        <p style={{ fontSize: '0.96rem', lineHeight: 1.7, color: '#4b5563', margin: 0 }}>{item.text}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-4">
            <div className="mb-3">
              <h2 style={{ fontSize: '2.1rem', fontWeight: 800, margin: 0 }}>More success stories</h2>
            </div>

            <div className="row g-4">
              {stories.map((item) => (
                <div className="col-md-4" key={item.title}>
                  <div style={{ background: '#fff', borderRadius: 18, padding: '1.5rem', boxShadow: '0 10px 28px rgba(0,0,0,0.04)', height: '100%' }}>
                    <h4 style={{ fontSize: '1.35rem', fontWeight: 700, marginBottom: '0.8rem' }}>{item.title}</h4>
                    <p style={{ fontSize: '0.98rem', lineHeight: 1.8, color: '#4b5563', margin: 0 }}>{item.text}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="mt-4 text-center">
            <Link to="/signup" className="btn btn-primary btn-lg px-4 py-3" style={{ borderRadius: 12 }}>
              Join SLAMMMM Today
            </Link>
          </div>
        </div>
      </section>
    </>
  );
};

export default LoveStories;
